'use client';

/** One capability tile in the capabilities modal grid. */

import React from 'react';
import { CATEGORY_LABELS, ROLE_LABELS } from './labels.js';
import { extraExamplePrompts } from './filters.js';
import { AutoApproveToggle } from './AutoApproveToggle.js';
import type { ChatToolCapabilityItem } from './types.js';

export interface CapabilityCardProps {
  tool: ChatToolCapabilityItem;
  /**
   * Standing-approval controls. Omitted entirely by a host that does not offer
   * them (or while the settings are still loading), in which case the approval
   * state renders as the read-only badge it always was.
   */
  autoApprove?: {
    /** True when this tool may be pre-authorized at all (server-derived). */
    grantable: boolean;
    isOn: boolean;
    isSaving: boolean;
    onChange: (next: boolean) => void;
  };
  /**
   * A generic pick-this-one checkbox, independent of `autoApprove` — for any
   * consumer that wants to SELECT capabilities from this same card rather than
   * render its own list item (e.g. a routine's write-policy editor choosing
   * which tools a routine may call). Mutually orthogonal to `autoApprove`:
   * a host may offer neither, either, or both.
   */
  selection?: {
    checked: boolean;
    onChange: (next: boolean) => void;
    /** Shown next to the checkbox when this pick would have no effect (e.g.
     * "the ops_researcher agent cannot call this"). Never disables the
     * checkbox itself — naming the mismatch is more legible than a control
     * that silently vanishes when the caller is about to change something
     * else that would make it usable again. */
    disabledReason?: string;
  };
}

export function CapabilityCard({ tool, autoApprove, selection }: CapabilityCardProps) {
  const extras = extraExamplePrompts(tool);
  const hasExamples = Boolean(tool.examplePrompt) || extras.length > 0;

  return (
    <li className="flex flex-col gap-2 min-w-0 rounded-lg border border-border bg-muted/30 px-3 py-2.5">
      <div className="flex flex-wrap items-center gap-1.5">
        {selection && (
          <input
            type="checkbox"
            checked={selection.checked}
            onChange={(e) => selection.onChange(e.target.checked)}
            aria-label={`Allow: ${tool.displayName}`}
            className="h-3.5 w-3.5 shrink-0 accent-primary"
          />
        )}
        <span className="text-sm font-medium text-foreground truncate">{tool.displayName}</span>
        <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground shrink-0">
          {CATEGORY_LABELS[tool.category] ?? tool.category}
        </span>
        {tool.requiresApproval &&
          (autoApprove?.grantable ? (
            <AutoApproveToggle
              toolName={tool.displayName}
              isOn={autoApprove.isOn}
              isSaving={autoApprove.isSaving}
              onChange={autoApprove.onChange}
            />
          ) : (
            <span
              className="rounded bg-amber-500/15 px-1.5 py-0.5 text-[10px] font-medium text-amber-700 dark:text-amber-400 shrink-0"
              title="You'll be asked to confirm before this runs"
            >
              Requires approval
            </span>
          ))}
      </div>

      <span className="text-xs text-muted-foreground line-clamp-2">{tool.useCase}</span>

      {selection?.disabledReason && (
        <span className="text-xs text-amber-700 dark:text-amber-400">
          {selection.disabledReason}
        </span>
      )}

      {hasExamples && (
        <div className="space-y-1">
          {tool.examplePrompt && (
            <p className="text-xs text-muted-foreground/90 line-clamp-2">
              <span className="font-medium text-foreground/80">Try: </span>
              &ldquo;{tool.examplePrompt}&rdquo;
            </p>
          )}
          {extras.map((prompt) => (
            <p key={prompt} className="text-xs text-muted-foreground/80 line-clamp-1">
              &ldquo;{prompt}&rdquo;
            </p>
          ))}
        </div>
      )}

      {tool.permittedRoles && tool.permittedRoles.length > 0 && (
        <div className="flex flex-wrap gap-1 pt-1 border-t border-border/50">
          {tool.permittedRoles.map((role) => (
            <span
              key={role}
              className="inline-flex items-center rounded-full bg-blue-500/10 px-2 py-0.5 text-[10px] font-medium text-blue-700 dark:text-blue-400"
              title={`Available to ${ROLE_LABELS[role] || role}`}
            >
              {ROLE_LABELS[role] || role}
            </span>
          ))}
        </div>
      )}
    </li>
  );
}
